<template>
  <Dialog v-model:open="open">
    <DialogTrigger as-child>
      <slot v-if="slots.trigger" name="trigger" />
      <Button v-else>
        <template #startIcon>
          <UserPlus />
        </template>
        Add Member
      </Button>
    </DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Invite people to this workspace</DialogTitle>
      </DialogHeader>
      <div v-if="!isLoading" class="px-7 pb-6 pt-5 w-full md:min-w-[450px]">
        <form id="invite-member" class="space-y-5" @submit="onSubmit">
          <fieldset class="flex flex-col gap-1">
            <Label for="email">Email</Label>
            <Input
              id="email"
              v-model="email"
              type="email"
              v-bind="emailAttrs"
              placeholder="Enter an email address"
            />
            <span class="text-error-400">{{ errors?.email }}</span>
          </fieldset>
          <fieldset class="flex flex-col gap-1">
            <Label for="role">Role</Label>
            <Select id="role" v-model="role" size="xl">
              <SelectTrigger class="w-auto">
                <SelectValue disabled placeholder="Select a role">
                  {{ roles.find(r => r.id === parseInt(role))?.name }}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="r in roles"
                  :key="r.id"
                  :value="r.id.toString()"
                  class="text-neutral-300 [&:not(:last-child)]:mb-2"
                >
                  {{ r.name }}
                  <div class="text-neutral-500">
                    <span>{{ r.description }}</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <span class="text-error-400">{{ errors.role }}</span>
          </fieldset>
        </form>
        <div
          v-if="currentSubscriptionPlan"
          class="text-neutral-400 text-xs max-w-[430px]"
        >
          Upon accepting the invitation, the workspace will be charged the
          prorated amount for the current monthly billing cycle.
        </div>
      </div>
      <DialogFooter>
        <DialogClose as-child>
          <Button variant="ghost" :disabled="isPending">Cancel</Button>
        </DialogClose>
        <Button
          type="submit"
          form="invite-member"
          :disabled="isPending"
          :loading="isPending"
        >
          Invite
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup>
import { ref, computed, useSlots } from 'vue';
import { useStore } from 'vuex';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogHeader,
  DialogFooter,
  DialogClose,
} from '@ui/components/dialog';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { useForm } from 'vee-validate';

import { UserPlus } from 'lucide-vue-next';

import requests from '@vuex/requests.js';

import { Label } from '@ui/components/label';
import { Input } from '@ui/components/input';
import { Button } from '@ui/components/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ui/components/select';
import { toast } from 'vue-sonner';
import { queries } from '@/queries/index.js';

const slots = useSlots();

const store = useStore();

const defaultRoleId = computed(
  () => store.getters['workspace/getDefaultRoleId']
);

const validationSchema = toTypedSchema(
  z.object({
    email: z.string().min(1, { message: 'Email is required' }).email(),
    role: z.string().refine(role => role !== 0, {
      message: 'Role is required',
    }),
  })
);

const currentSubscriptionPlan = computed(
  () => store.getters['workspace/getCurrentSubscriptionPlan']
);

const open = ref(false);
const queryClient = useQueryClient();

const { handleSubmit, errors, defineField, resetForm } = useForm({
  validationSchema,
  initialValues: {
    email: '',
    role: defaultRoleId.value.toString(),
  },
});
const [email, emailAttrs] = defineField('email');
const [role] = defineField('role');

const { isLoading, data: roles } = useQuery(queries.workspace.roles);

const { isPending, mutate: inviteUser } = useMutation({
  mutationFn: async payload => {
    await requests.invitations.create(payload);
    return payload;
  },
  onSuccess: async data => {
    await queryClient.invalidateQueries({
      queryKey: queries.workspace.invitations({ status: ['PENDING'] }).queryKey,
    });
    resetForm();
    open.value = false;
    toast.success(`Invitation sent to ${data.email}`);
  },
  onError: data => {
    if (data?.response?.data?.message) {
      toast.error(data.response.data.message);
    }
  },
});

const onSubmit = handleSubmit(values => {
  inviteUser({
    ...values,
    workspace_id: store.getters['workspace/getId'],
  });
});
</script>
